import { useState } from 'react';
import { Eye, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { communicationsApi, type CommunicationsRecipient } from '../../lib/api';

type Empfaenger = { id: string; name: string; backend: CommunicationsRecipient };

/**
 * Vorschau einer Nachricht mit eingesetzten Variablen (T-0141).
 *
 * Gerendert wird auf dem Server mit denselben Platzhaltern wie beim Versand —
 * Schulname, Standort, Empfänger, Schülername —, für einen gewählten Empfänger.
 * Bisher sah man im Editor nur {{studentFirstName}} und musste raten.
 */
export default function NachrichtVorschau({
  standortId,
  purpose,
  kanal,
  empfaenger,
  entwurf,
  vars,
}: Readonly<{
  standortId: number;
  purpose: 'transactional' | 'marketing';
  kanal: 'email' | 'sms';
  empfaenger: Empfaenger[];
  entwurf: Record<string, string | undefined>;
  vars?: Record<string, unknown>;
}>) {
  const [offen, setOffen] = useState(false);
  const [empfaengerId, setEmpfaengerId] = useState<string>('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ergebnis, setErgebnis] = useState<{ subject?: string; html?: string; text?: string } | null>(null);

  const zeigen = async (id: string) => {
    const ziel = empfaenger.find((e) => e.id === id) ?? empfaenger[0];
    if (!ziel) return;
    setEmpfaengerId(ziel.id);
    setOffen(true);
    setLaeuft(true);
    setFehler('');
    try {
      const antwort = await communicationsApi.preview({
        channel: kanal,
        standortId,
        purpose,
        recipient: ziel.backend,
        draft: entwurf,
        vars,
      });
      setErgebnis({
        subject: typeof antwort.subject === 'string' ? antwort.subject : undefined,
        html: typeof antwort.html === 'string' ? antwort.html : undefined,
        text: typeof antwort.text === 'string' ? antwort.text : undefined,
      });
    } catch (e) {
      setErgebnis(null);
      setFehler(e instanceof Error ? e.message : 'Vorschau fehlgeschlagen');
    } finally {
      setLaeuft(false);
    }
  };

  if (empfaenger.length === 0) return null;

  return (
    <div className="space-y-2">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => (offen ? setOffen(false) : void zeigen(empfaengerId || empfaenger[0].id))}
      >
        {offen ? <X className="mr-1 h-4 w-4" /> : <Eye className="mr-1 h-4 w-4" />}
        {offen ? 'Vorschau schließen' : 'Vorschau'}
      </Button>
      {offen ? (
        <div className="rounded-md border p-3 space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <span className="text-muted-foreground">So kommt es an bei</span>
            {empfaenger.length > 1 ? (
              <select
                className="rounded-md border bg-background px-2 py-1 text-sm"
                value={empfaengerId}
                onChange={(e) => void zeigen(e.target.value)}
              >
                {empfaenger.map((e) => (
                  <option key={e.id} value={e.id}>{e.name}</option>
                ))}
              </select>
            ) : (
              <span className="font-medium">{empfaenger[0].name}</span>
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => void zeigen(empfaengerId)} disabled={laeuft}>
              Aktualisieren
            </Button>
          </div>
          {laeuft ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Vorschau wird erstellt…
            </div>
          ) : fehler ? (
            <p className="text-sm text-destructive">{fehler}</p>
          ) : ergebnis ? (
            kanal === 'email' ? (
              <div className="space-y-2">
                <div className="text-sm">
                  <span className="text-muted-foreground">Betreff: </span>
                  <span className="font-medium">{ergebnis.subject || '—'}</span>
                </div>
                <iframe
                  title="Vorschau der E-Mail"
                  sandbox=""
                  srcDoc={ergebnis.html || ''}
                  className="h-96 w-full rounded border bg-white"
                />
              </div>
            ) : (
              <pre className="whitespace-pre-wrap rounded bg-muted p-3 text-sm">{ergebnis.text || ''}</pre>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
